import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { collection, query, orderBy, getDocs, Timestamp, doc, getDoc } from "firebase/firestore";
import { db, auth } from "../firebase";
import { IoMdNotifications } from "react-icons/io";
import { GiHamburgerMenu } from "react-icons/gi";
import Sidebar from "../components/authPage/structures/Sidebar";
import Bottombar from "@/components/authPage/structures/Bottombar";
import { BookOpen, Gift, Calendar, Bell } from 'lucide-react';
import Feed from "./components/Feed";
import NewPostForm from "@/components/Forms/NewPostForm";
import { useMobileContext } from "@/contexts/MobileContext";
import QuickActionsButton from "./components/QuickAction";
import { Skeleton } from "@/components/ui/skeleton";
import Stories from "@/components/Stories/Stories";

// type FilterType = "all" | "need" | "offer";

interface Post {
  id: string;
  title: string;
  category: string;
  description: string;
  urgencyLevel: number;
  photoUrls: string[];
  location: string;
  coordinates: {
    lat: number;
    lng: number;
  } | null;
  userId: string;
  postType: "need" | "offer";
  duration: string;
  visibilityRadius: number;
  isAnonymous: boolean;
  createdAt: Timestamp;
  userName?: string;
  userPhoto?: string;
  distance?: number;
}

const Home: React.FC = () => {
  const [, setPosts] = useState<Post[]>([]);
  const [, setLoading] = useState(true);
  const [radius, setRadius] = useState(3);
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [, setHasEmergencyAlerts] = useState(false);
  const [, setEmergencyAlerts] = useState<Post[]>([]);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [userDetails, setUserDetails] = useState<any>(null);
  const [updated,] = useState(false);
  const navigate = useNavigate();
  const { isMobile } = useMobileContext();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [postType, setPostType] = useState<'resource' | 'event' | 'promotion' | 'update' | null>(null);

  const openModal = (type?: 'resource' | 'event' | 'promotion' | 'update') => {
    setPostType(type || null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };



  const handleSuccess = () => {
    console.log('Post created successfully!');
    // You can implement additional success handling here
  };

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        (error) => {
          console.error("Error getting location:", error);
        }
      );
    }
  }, []);

  useEffect(() => {
    if (!userLocation) return;

    const fetchPosts = async () => {
      setLoading(true);
      try {

        const postsRef = collection(db, "posts");
        const q = query(postsRef, orderBy("createdAt", "desc"));
        const querySnapshot = await getDocs(q);

        const emergencyData: Post[] = [];

        const postsWithUserPromises = querySnapshot.docs.map(async (document) => {
          const data = document.data() as Omit<Post, "id">;
          const post = { id: document.id, ...data } as Post;

          if (post.userId) {
            try {
              const userDoc = await getDoc(doc(db, "Users", post.userId));
              if (userDoc.exists()) {
                const userData = userDoc.data();

                post.userName = userData.firstName ? `${userData.firstName} ${userData.lastName}` : userData.firstName || userData.lastName || userData.displayName || "User";
                post.userPhoto = userData.photo || null;
              }

            } catch (userError) {
              console.error("Error fetching user data:", userError);
            }
          }

          if (post.coordinates && userLocation) {
            post.distance = calculateDistance(
              userLocation.lat,
              userLocation.lng,
              post.coordinates.lat,
              post.coordinates.lng
            );

            if (post.distance <= radius) {
              if (post.urgencyLevel === 3) {
                emergencyData.push(post);
              }
              return post;
            }
          } else {
            return post;
          }
          return null;
        });

        const resolvedPosts = await Promise.all(postsWithUserPromises);

        const filteredPosts = resolvedPosts.filter(post => post !== null) as Post[];

        setPosts(filteredPosts);
        setEmergencyAlerts(emergencyData);
        setHasEmergencyAlerts(emergencyData.length > 0);
      } catch (error) {
        console.error("Error fetching posts:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, [userLocation, radius, updated]);

  useEffect(() => {
    const fetchUserData = async () => {
      auth.onAuthStateChanged(async (user) => {
        if (user) {
          const docRef = doc(db, "Users", user.uid);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            setUserDetails(docSnap.data());
          } else {
            // navigate("/login");
            console.log("No such document!");
          }
        }
      });
    };

    fetchUserData();
  }, []);

  async function handleLogout() {
    try {
      await auth.signOut();
      window.location.href = "/login";
    } catch (error: unknown) {
      if (error instanceof Error) {
        console.error("Error logging out:", error.message);
      }
    }
  }

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number) => {
    const R = 6371;
    const dLat = deg2rad(lat2 - lat1);
    const dLon = deg2rad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;
    return distance;
  };

  const deg2rad = (deg: number) => {
    return deg * (Math.PI / 180);
  };

  const RadiusDropdown: React.FC<{ radius: number; setRadius: React.Dispatch<React.SetStateAction<number>> }> = ({ radius, setRadius }) => {
    const [open, setOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const options = [1, 3, 5, 10];

    const handleOutside = useCallback((e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }, []);

    useEffect(() => {
      document.addEventListener('mousedown', handleOutside);
      return () => document.removeEventListener('mousedown', handleOutside);
    }, [handleOutside]);

    return (
      <div ref={containerRef} className="relative">
        <button
          onClick={() => setOpen((s) => !s)}
          className="flex items-center gap-2 px-3 py-1 rounded-full bg-white dark:bg-gray-800 text-sm shadow-sm"
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <span className="text-xs text-gray-700 dark:text-gray-200">{radius} km</span>
          <svg className="h-3 w-3 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        <div
          className={`absolute right-0 mt-2 w-36 rounded-md bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-lg z-50 overflow-hidden transform origin-top-right transition-all duration-200 ${open ? 'opacity-100 scale-100 translate-y-0 pointer-events-auto' : 'opacity-0 scale-95 -translate-y-1 pointer-events-none'}`}
          role="listbox"
          aria-hidden={!open}
        >
          {options.map((opt) => (
            <button
              key={opt}
              onClick={() => { setRadius(opt); setOpen(false); }}
              className={`w-full text-left px-3 py-2 text-sm ${opt === radius ? 'bg-indigo-50 dark:bg-indigo-900/30 font-semibold' : 'hover:bg-gray-100 dark:hover:bg-gray-700'}`}
            >
              {opt} km
            </button>
          ))}
        </div>
      </div>
    );
  };

  return (
    <>
      {
        userDetails ? (
          <div className={`flex flex-col min-h-screen ${isMobile ? "mb-16" : ""} bg-purple-50 dark:bg-gray-900`}>
            {/* Responsive Sideb</div>ar */}
            <div
              className={`fixed inset-y-0 left-0 w-64 transform ${isSidebarOpen ? "translate-x-0" : "-translate-x-full"} md:translate-x-0 transition-transform duration-300 z-100`}
            >
              <Sidebar
                // userDetails={userDetails}
                handleLogout={handleLogout}
                isSidebarOpen={isSidebarOpen}
              />
            </div>

            {/* Overlay to close sidebar when clicking outside (only on mobile) */}
            {isSidebarOpen && (
              <div
                className="fixed inset-0 bg-transparent z-30 md:hidden"
                onClick={toggleSidebar}
              />
            )}

            {/* Main Content Area */}
            <div className="md:ml-64 pt-16">
              {/* Top Navigation (fixed) */}
              <div className="fixed top-0 md:ml-64 left-0 right-0 z-50 bg-white dark:bg-gray-900 border-b border-gray-200/10">
                <div className="flex items-center justify-between p-3 max-w-7xl mx-auto">
                  <div
                    className="flex items-center gap-5 space-x-2 cursor-pointer"
                    onClick={toggleSidebar}
                  >
                    <GiHamburgerMenu className="text-2xl text-gray-700 dark:text-gray-200" />
                    {
                      !isMobile && (
                        <div>
                          <h2 className="text-xs font-medium text-gray-600 dark:text-gray-300">Your Neighborhood</h2>
                          <p className="text-[10px] font-semibold text-indigo-700 dark:text-indigo-300">
                            {userLocation ? "Current Location" : "Location unavailable"}
                          </p>
                        </div>
                      )
                    }
                  </div>

                  <div className="flex  items-center justify-center ">
                    <h1 className="text-xl font-bold text-blue-800 dark:text-blue-700">Neighbour</h1>
                    <h1 className="text-xl font-bold text-violet-800 dark:text-violet-700 ml-1">Link</h1>
                  </div>

                  <div className="flex items-center space-x-3">
                    {/* Radius selector merged into header (glassy) */}
                    <div className="hidden sm:flex items-center space-x-3 px-3 py-1 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm">
                      <div className="flex items-center gap-2">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 11c1.656 0 3-1.567 3-3.5S13.656 4 12 4s-3 1.567-3 3.5S10.344 11 12 11z" />
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 21s8-4.5 8-10.5S16.97 3 12 3 4 6.5 4 10.5 12 21 12 21z" />
                        </svg>
                        <span className="text-xs text-gray-600 dark:text-gray-300">Radius</span>
                      </div>

                      <RadiusDropdown radius={radius} setRadius={setRadius} />
                    </div>

                    <button
                      onClick={() => navigate("/notifications")}
                      aria-label="Open notifications"
                      className="p-2 rounded-full bg-gradient-to-tr from-purple-600 via-indigo-600 to-purple-500 shadow-xl ring-1 ring-purple-300/30 hover:scale-105 transform transition-all duration-200"
                    >
                      <IoMdNotifications className="text-2xl text-white" />
                    </button>
                  </div>
                </div>

                {/* Neighborhood, Radius Selector, and Filter
                <div className="flex items-center justify-between px-4 py-2 bg-indigo-50 dark:bg-indigo-900">
                </div> */}

              </div>

              {/* Quick Actions Grid */}
              <div className="flex justify-center items-center">
                <QuickActionsButton openModal={openModal} setIsSidebarOpen={setIsSidebarOpen} />
              </div>

              {/* Stories Section */}
              <Stories onStoryOpen={() => setIsSidebarOpen(false)} />

              <div className="flex-1 px-4 py-2 ">
                <Feed radius={radius} />
              </div>

              {/* Floating Action Button */}
              <FloatingActionMenu openModal={openModal} />

              {/* Floating image for PujoGuide navigation (replaces previous FAB)
              {/* Animated gradient ring (conic) behind the button; keyframes defined inline below */}
              
              
              {/* <div className="fixed bottom-6 right-6 z-50">
                
                <style>{`@keyframes spinRing { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
                <div className="relative w-[78px] h-[78px]">
                  <span
                    className="absolute inset-0 rounded-full z-0"
                    style={{
                      background: 'conic-gradient(#3b82f6, #8b5cf6, #f59e0b, #3b82f6)',
                      filter: 'blur(22px)',
                      WebkitFilter: 'blur(10px)',
                      animation: 'spinRing 3s linear infinite'
                    }}
                  />

                  <button
                    onClick={() => navigate('/pujo-planner')}
                    aria-label="Go to PujoGuide"
                    className="relative z-10 w-full h-full rounded-full bg-white overflow-hidden shadow-xl border-0 p-0 flex items-center justify-center"
                  >
                    <img
                      src="/assets/dhak.jpg"
                      alt="Pujo Guide"
                      className="w-full h-full object-cover block rounded-full"
                    />
                  </button>
                </div>
              </div>  */}

              <NewPostForm
                isOpen={isModalOpen}
                onClose={closeModal}
                initialPostType={postType}
                onSuccess={handleSuccess}
              />

              {
                isMobile && (
                  <Bottombar />
                )
              }

            </div>
          </div>
        ) : (
          <div className="flex flex-col space-y-4">
            <div className="flex items-center p-4 space-x-4">
              <Skeleton className="h-12 w-12 rounded-full" />
              <div className="space-y-2">
                <Skeleton className="h-4 w-[200px]" />
                <Skeleton className="h-4 w-[150px]" />
              </div>
            </div>
            <div className="p-4 space-y-4">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-[90%]" />
              <Skeleton className="h-4 w-[80%]" />
            </div>
            <div className="p-4">
              <Skeleton className="h-[200px] w-full rounded-xl" />
            </div>
            <div className="p-4 space-y-3">
              <Skeleton className="h-4 w-[70%]" />
              <Skeleton className="h-4 w-[60%]" />
            </div>
          </div>
        )
      }
    </>
  );
};

export default Home;

export const FloatingActionMenu: React.FC<{ 
  openModal: (type?: 'resource' | 'event' | 'promotion' | 'update') => void;
}> = ({ openModal }) => {
  const [isOpen, setIsOpen] = useState(false);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const menuOptions = [
    { icon: <BookOpen size={24} />, label: "Resource", id: "resource" },
    { icon: <Gift size={24} />, label: "Promotion", id: "promotion" },
    { icon: <Calendar size={24} />, label: "Event", id: "event" },
    { icon: <Bell size={24} />, label: "Local Updates", id: "update" }
  ];

  return (
    <div className="relative">
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 bg-opacity-50 z-40 transition-opacity duration-300"
          onClick={toggleMenu}
        />
      )}

      <div className="fixed inset-0 flex items-center justify-center z-50 pointer-events-none">
        <div className="grid grid-cols-2 gap-8">
          {menuOptions.map((option, index) => (
            <button
              key={option.id}
              className={`flex flex-col items-center justify-center p-6 bg-white rounded-lg shadow-md border border-gray-300 pointer-events-auto transition-all duration-300 hover:shadow-lg hover:bg-gradient-to-r hover:from-blue-50 hover:to-teal-50 ${isOpen ? 'opacity-100' : 'opacity-0'
                }`}
              style={{
                transform: isOpen
                  ? 'translate(0, 0) scale(1)'
                  : 'translate(calc(100vw - 5rem - 50%), calc(100vh - 20rem - 50%)) scale(0.1)',
                transition: `transform 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275) ${index * 0.1
                  }s, opacity 0.3s ease ${index * 0.1}s`,
                minWidth: '120px',
                minHeight: '140px',
              }}
              onClick={() => {openModal(option.id as 'resource' | 'event' | 'promotion' | 'update'); toggleMenu()}}
              aria-label={`Go to ${option.label} form`}
            >
              <div className="h-16 w-16 rounded-full bg-gradient-to-tr from-blue-600 to-teal-500 flex items-center justify-center mb-3 shadow-md">
                <div className="text-white">
                  {option.icon}
                </div>
              </div>
              <span className="text-base font-semibold text-gray-800">{option.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* <button
        className={`fixed ${isMobile ? "bottom-20 right-5" : "bottom-8 right-8"} h-14 w-14 rounded-full bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center shadow-lg z-50 transition-transform duration-300`}
        onClick={()=>{setIsSidebarOpen(false); toggleMenu()}}
        aria-label="Open menu"
      >
        <Plus size={28} className={`transition-transform duration-300 ${isOpen ? 'rotate-45' : 'rotate-0'}`} />
      </button> */}
    </div>
  );
};
